import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PlusCircle, Edit, Trash2, Users, GripVertical, MapPin, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import RegistrationsDialog from './RegistrationsDialog';

const BACKEND_URL = '';

export function AdminCoursesTab() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrollmentsFor, setEnrollmentsFor] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => { loadCourses(); }, []);

  const loadCourses = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/courses`, { credentials: 'include' });
      if (res.ok) setCourses(await res.json());
    } catch {}
  };

  const handleDeleteCourse = async (courseId) => {
    if (!(await window.appConfirm('Are you sure you want to delete this course?'))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/courses/${courseId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted successfully');
      loadCourses();
    } catch { toast.error('Failed to delete'); }
  };

  const persistOrder = async (orderedIds) => {
    setSavingOrder(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/courses/order`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: orderedIds }),
      });
      if (!res.ok) throw new Error('Reorder failed');
      toast.success('Course order saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save order');
      loadCourses();
    }
    setSavingOrder(false);
  };

  const handleDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const items = [...courses];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(idx, 0, moved);
    setCourses(items);
    setDragIdx(null);
    persistOrder(items.map(c => c.course_id));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button data-testid="create-course-button" onClick={() => navigate('/admin/courses/new')} className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
          <PlusCircle className="h-4 w-4 mr-2" /> Create Course
        </Button>
        <p className="text-xs text-slate-500">
          {savingOrder ? 'Saving order…' : 'Drag the handle on the left of each card to reorder courses.'}
        </p>
      </div>

      <div className="grid gap-3" data-testid="admin-courses-list">
        {courses.map((course, idx) => {
          const isInPerson = course.delivery_mode === 'in-person';
          const isHybrid = course.delivery_mode === 'hybrid';
          return (
            <Card
              key={course.course_id}
              data-testid={`admin-course-${course.course_id}`}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(idx)}
              onDragEnd={() => setDragIdx(null)}
              className={`p-5 flex gap-3 items-start transition-all ${dragIdx === idx ? 'opacity-40 scale-[0.99]' : ''} cursor-grab active:cursor-grabbing`}
            >
              <div className="flex-shrink-0 mt-1.5 text-slate-300 hover:text-slate-500" title="Drag to reorder">
                <GripVertical className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="text-lg font-bold font-heading truncate">{course.title}</h3>
                  {isInPerson && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> In-Person
                    </span>
                  )}
                  {isHybrid && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Hybrid</span>
                  )}
                  {!isInPerson && !isHybrid && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                      <Monitor className="h-3 w-3" /> Online
                    </span>
                  )}
                  {course.coming_soon && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Coming soon</span>
                  )}
                  {(course.price || 0) > 0 && (
                    <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">£{course.price}</span>
                  )}
                </div>
                <p className="text-sm text-slate-600 line-clamp-2 mb-2">{course.intro || course.description}</p>
                <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  <span>Level: {course.level || '—'}</span>
                  {!isInPerson && <span>{course.modules?.length || 0} modules</span>}
                  {!isInPerson && course.exam?.questions?.length > 0 && (
                    <span className="text-brand-navy">{course.exam.questions.length} exam questions</span>
                  )}
                  {isInPerson && course.venue && <span><MapPin className="h-3 w-3 inline mr-1" />{course.venue}</span>}
                  {isInPerson && course.sessions?.length > 0 && (
                    <span className="text-brand-navy font-medium" data-testid={`course-session-count-${course.course_id}`}>
                      {course.sessions.length} session{course.sessions.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {isInPerson && !course.sessions?.length && course.course_dates && <span>{course.course_dates}</span>}
                  <span>{course.enrolled_users?.length || 0} enrolled</span>
                </div>
              </div>
              <div className="flex space-x-2 flex-shrink-0">
                <Button onClick={(e) => { e.stopPropagation(); setEnrollmentsFor({ id: course.course_id, title: course.title }); }} variant="outline" size="sm" data-testid={`view-course-enrollments-${course.course_id}`} title="View enrolled members">
                  <Users className="h-4 w-4 mr-1" />
                  <span className="text-xs tabular-nums">{course.enrolled_users?.length || 0}</span>
                </Button>
                <Button onClick={(e) => { e.stopPropagation(); navigate(`/admin/courses/${course.course_id}/edit`); }} variant="outline" size="sm" data-testid={`edit-course-${course.course_id}`}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button onClick={(e) => { e.stopPropagation(); handleDeleteCourse(course.course_id); }} variant="outline" size="sm" className="text-red-600">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <RegistrationsDialog
        open={!!enrollmentsFor}
        onClose={() => setEnrollmentsFor(null)}
        kind="course"
        id={enrollmentsFor?.id}
        title={enrollmentsFor?.title}
      />
    </div>
  );
}
